<template>
	<div>
		<Disclosure :title="$t('text.publicInfoTitle')" :error="sectionMessage.error" :message="sectionMessage.message" :lock="!userMayEditFields" data-cy="publicInfo" ref="SECTION_publicInfo">
			<div class="field left-border">
				<v-label>{{$t('text.faq')}}</v-label>
				<v-select variant="outlined" density="compact" return-object
					hide-details
					:items="faqGroups"
					:item-title="item => item && item.fields && item.fields.title ? item.fields.title[selectedLocale] : ''"
					v-model="model.fields.faqGroup.de"
				/>
				<p class="helpText" v-html="$t('text.faqBusinessProfileDesc')"/>
			</div>

			<Field typeName="BusinessProfile" fieldName="longDescription" v-model="model.fields.longDescription" dataCy="longDescription" />
			<Field typeName="BusinessProfile" fieldName="shortDescription" v-model="model.fields.shortDescription" dataCy="shortDescription" />
			<Field typeName="BusinessProfile" fieldName="importantInformation" v-model="model.fields.importantInformation" dataCy="importantInfo" />

			<div v-if="$store.state.selectedServiceProvider" class="field left-border">
				<v-label>{{ $t('text.images') }}</v-label>
				<ImagesField v-model="model.fields.imageGallery.de" @toggle-loading="toggleLoading" />
			</div>

			<!-- MYS-7340 -->
			<Field ref="ctaButtonsField" class="field ctaField" typeName="BusinessProfile" fieldName="ctaButtons" v-model="model.fields.ctaButtons" :disabled="!userMayEditFields" />
		</Disclosure>
	</div>
</template>

<script>
import Common from '@/mixins/Common.vue'
import Disclosure from '@/components/common/Disclosure.vue'
import Field from '@/components/fields/Field.vue'
import ImagesField from '@/components/common/ImagesField.vue'
import isEqual from 'lodash/isEqual'
import { ctaEntryErrors, fromCtaEntries, toCtaEntries } from '@/utils/cta-buttons'

export default {
	name: 'PublicInfo',
	components: { Disclosure, Field, ImagesField },
	mixins: [ Common ],
	emits: ['isLoading', 'update-translation-fields', 'validity-changed'],
	props: {
		serviceProvider: { type: Object, required: true },
		updateModel: Boolean,
		runValidation: Boolean
	},
	data: () => ({
		model: {},
		initData: {},
		sectionMessage: {
			error: false,
			message: ''
		},
		faqGroups: [],
		lastValidationResult: null,
	}),
	watch: {
		model: {
			deep: true,
			handler(model) {
				if (model?.fields) {
					this.$emit('update-translation-fields', model.fields)
				}
				const isValid = this.validateAllFields()
				if (this.lastValidationResult !== isValid) {
					this.lastValidationResult = isValid
					this.$emit('validity-changed', isValid)
				}
			}
		},
		updateModel() {
			this.model = this.valueToModel(this.serviceProvider)

			if (this.$store.state.selectedServiceProvider?.sys?.id) {
				this.getFAQs()
			}
			this.setInitData()
		}
	},
	methods: {
		toggleLoading(value) {
			this.$emit('isLoading', value)
		},
		valueToModel(v) {
			const model = JSON.parse(JSON.stringify(v ?? {}))
			if (model.fields) {
				model.fields.ctaButtons = { de: toCtaEntries(model.fields.ctaButtons?.de) }
			}
			return model
		},
		setInitData() {
			const initModel = JSON.parse(JSON.stringify(this.model))
			this.initData = {
				faqGroup: initModel.fields.faqGroup,
				longDescription: initModel.fields.longDescription,
				shortDescription: initModel.fields.shortDescription,
				importantInformation: initModel.fields.importantInformation,
				imageGallery:initModel.fields.imageGallery,
				ctaButtons: { de: fromCtaEntries(initModel.fields.ctaButtons?.de) },
			}
		},
		sendData() {
			let data = {
				faqGroup: this.model.fields.faqGroup,
				longDescription: this.model.fields.longDescription,
				shortDescription: this.model.fields.shortDescription,
				importantInformation: this.model.fields.importantInformation,
				imageGallery: this.model.fields.imageGallery,
				ctaButtons: { de: fromCtaEntries(this.model.fields.ctaButtons?.de) },
			}

			data.changed = !isEqual(data, this.initData)
			return data
		},
		async getFAQs() {
			try {
				//let res = await this.$httpGet(`/faqs?serviceProviderId=${this.$store.state.selectedServiceProvider.sys.id}`)

				this.faqGroups = this.$store.state.selectedServiceProvider.fields.faqGroups?.de ?? []
				if (this.faqGroups) {this.faqGroups.sort(this.compare)}

				//add an empty object to the top to make possible a deselect for v-select as v-select does not have a built-in way to select a blank option
				this.faqGroups.unshift({})
			}
			catch (error) {
				this.showError(error)
			}
		},
		validateAllFields() {
			this.resetSectionError(this.sectionMessage)
			if (!this.hasRequiredDescriptions() || !this.hasValidCtaButtons()) {
				if (this.runValidation) {
					this.setSectionError(this.sectionMessage, this.$t('text.missingFieldsError'))
					// from now on the field shows which button is incomplete
					this.$refs.ctaButtonsField?.$refs?.field?.validate()
				}
				return false
			}
			return true
		},
		hasValidCtaButtons() {
			const defaultLocaleCode = this.contentLocaleCode()
			return (this.model.fields?.ctaButtons?.de ?? []).every(entry => ctaEntryErrors(entry, defaultLocaleCode).length === 0)
		},
		hasRequiredDescriptions() {
			// Whitespace-only content (e.g. a single space) is intentionally treated as filled;
			// only a fully empty value (no text between tags) is rejected.
			const hasText = (value) => String(value ?? '')
				.replace(/<[^>]*>/g, '')
				.replace(/&nbsp;/g, ' ')
				.length > 0

			const locale = this.contentLocaleCode()
			return hasText(this.model.fields.longDescription?.[locale]) && hasText(this.model.fields.shortDescription?.[locale])
		},
	},
	created() {
		this.model = this.valueToModel(this.serviceProvider)

		if (this.$store.state.selectedServiceProvider?.sys?.id) {
			this.getFAQs()
		}

		this.setInitData()
	},
}
</script>
	
<style scoped>
.tiptap-vuetify-editor__content { height: 300px !important; }
.tiptap-vuetify-editor .ProseMirror { height: 300px !important; }
.enabled-text-style >>> .tiptap-vuetify-editor__content{ background: #ffffff !important; height: 300px; }
.serviceLocale { position: absolute; z-index: 10; margin-top: 32px; margin-left: -27px; zoom: 0.8; }
</style>

<style scoped lang="scss">
.ctaField :deep(.title-container .title) {
	color: #000;
	font-size: 17px;
	font-weight: 500;
	line-height: 22px;
	letter-spacing: -0.071px;
}
</style>
